import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

export type UserRole = 'student' | 'staff' | 'admin' | 'super-admin';

export interface SurveyUser {
  id: number;
  email: string;
  name: string;
  avatar?: string | null;
  surveyType: string;
  gradeLevel: number | null;
  schoolType: 'site-based' | 'virtual';
  gradeDetectionConfidence?: 'high' | 'medium' | 'low';
  role: UserRole;
  schoolName: string | null;
}

/**
 * Check if a role has admin-level access
 */
export function isAdminRole(role: UserRole | undefined): boolean {
  return role === 'admin' || role === 'super-admin';
}

/**
 * Check if a role has at least staff-level access
 */
export function isStaffOrHigher(role: UserRole | undefined): boolean {
  return role === 'staff' || role === 'admin' || role === 'super-admin';
}

interface AuthState {
  user: SurveyUser | null;
  token: string | null;
  completedSurveys: string[];
  availableSurvey: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
}

interface AuthContextType extends AuthState {
  login: (token: string, user: SurveyUser, completedSurveys: string[], availableSurvey: string) => void;
  logout: () => void;
  updateGrade: (gradeLevel: number, schoolType: 'site-based' | 'virtual') => Promise<void>;
  checkAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const TOKEN_KEY = 'survey_auth_token';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    completedSurveys: [],
    availableSurvey: null,
    isLoading: true,
    isAuthenticated: false,
  });

  const login = useCallback((token: string, user: SurveyUser, completedSurveys: string[], availableSurvey: string) => {
    localStorage.setItem(TOKEN_KEY, token);
    setState({
      user,
      token,
      completedSurveys,
      availableSurvey,
      isLoading: false,
      isAuthenticated: true,
    });
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setState({
      user: null,
      token: null,
      completedSurveys: [],
      availableSurvey: null,
      isLoading: false,
      isAuthenticated: false,
    });
  }, []);

  const checkAuth = useCallback(async (): Promise<boolean> => {
    const storedToken = localStorage.getItem(TOKEN_KEY);

    if (!storedToken) {
      setState(prev => ({ ...prev, isLoading: false, isAuthenticated: false }));
      return false;
    }

    try {
      const response = await fetch('/api/auth/verify', {
        headers: {
          'Authorization': `Bearer ${storedToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Token verification failed');
      }

      const data = await response.json();
      setState({
        user: data.user,
        token: storedToken,
        completedSurveys: data.completedSurveys || [],
        availableSurvey: data.availableSurvey,
        isLoading: false,
        isAuthenticated: true,
      });
      return true;
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setState({
        user: null,
        token: null,
        completedSurveys: [],
        availableSurvey: null,
        isLoading: false,
        isAuthenticated: false,
      });
      return false;
    }
  }, []);

  const updateGrade = useCallback(async (gradeLevel: number, schoolType: 'site-based' | 'virtual') => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    if (!storedToken) {
      throw new Error('Not authenticated');
    }

    const response = await fetch('/api/auth/select-grade', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${storedToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ gradeLevel, schoolType }),
    });

    if (!response.ok) {
      throw new Error('Failed to update grade');
    }

    const data = await response.json();

    // Update with new token and survey info
    localStorage.setItem(TOKEN_KEY, data.token);
    setState(prev => ({
      ...prev,
      token: data.token,
      availableSurvey: data.surveyId,
      user: prev.user ? {
        ...prev.user,
        gradeLevel,
        schoolType,
        surveyType: data.surveyType,
      } : null,
    }));
  }, []);

  // Check auth on mount
  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{
      ...state,
      login,
      logout,
      updateGrade,
      checkAuth,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
